import { useEffect, useRef, useState } from 'react';

type UseContainerBreakpointProps = {
  threshold?: number;
  hysteresisFactor?: number;
};

/**
 * Observes the width of a container element and returns whether it is
 * currently below the breakpoint threshold.
 *
 * `hysteresisFactor` prevents feedback loops where compact styles cause the
 * container to resize back above the threshold:
 * - Activates when width < threshold
 * - Deactivates when width > threshold * hysteresisFactor (default 1.1x)
 */
const useContainerBreakpoint = (args: UseContainerBreakpointProps = {}) => {
  const { threshold = 500, hysteresisFactor = 1.1 } = args;

  const ref = useRef<HTMLDivElement>(null);
  const [isBelowBreakpoint, setIsBelowBreakpoint] = useState(false);

  useEffect(() => {
    const el = ref.current;

    if (!el) return;

    const observer = new ResizeObserver(([entry]) => {
      const width = entry.contentRect.width;

      setIsBelowBreakpoint((prev) => {
        if (prev) return width < threshold * hysteresisFactor;

        return width < threshold;
      });
    });

    observer.observe(el);

    return () => observer.disconnect();
  }, [threshold, hysteresisFactor]);

  return { ref, isBelowBreakpoint };
};

export default useContainerBreakpoint;
